// ─── Écran STATISTIQUES : répartition du temps de garde ───────────
// Porté de MoPCal (`screens/StatistiquesScreen.js`), adapté à la suite :
//   • la garde vient de `calculerGardePeriode` (rythmes + vacances +
//     surcharges — la MÊME priorité que l'affichage du calendrier), pas du
//     planning brut stocké : MoPCal écrivait chaque jour en base, la suite
//     les CALCULE à la volée. Lire le planning brut aurait sous-compté
//     presque tous les jours d'un calendrier à rythme.
//   • pas de sélecteur de calendrier interne : l'écran suit le calendrier
//     ACTIF (`calId`), comme Journal/Contacts/Lieux/Historique.
// Lecture seule.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { PageHeader, EmptyState, GesteRetourEcran, useUI, useT } from '@mop/core';
import { calculerGardePeriode } from './services/gardePeriode';
import { PERIODES_STATS, bornesPeriode, calculerRepartition, repartitionParMois } from './services/statsGarde';

const COUL_P1 = '#6C4FE6';
const COUL_P2 = '#E91E63';

export default function EcranStatistiques({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [periode, setPeriode] = useState('3m');
  const [donnees, setDonnees] = useState(null);   // { parJour, parents } | null = en cours
  const [erreur, setErreur] = useState('');

  const jours = PERIODES_STATS.find(p => p.id === periode)?.jours || 90;
  const bornes = useMemo(() => bornesPeriode(jours), [jours]);

  useEffect(() => {
    if (!calId) { setDonnees({ parJour: {}, parents: null }); return; }
    let vivant = true;
    setDonnees(null); setErreur('');
    calculerGardePeriode({ calId, debut: bornes.debut, fin: bornes.fin })
      .then(r => { if (vivant) setDonnees(r); })
      .catch(e => { if (vivant) { setDonnees({ parJour: {}, parents: null }); setErreur(e?.message || String(e)); } });
    return () => { vivant = false; };
  }, [calId, bornes.debut, bornes.fin]);

  const parJour = donnees?.parJour || {};
  const parents = donnees?.parents;
  const estEnfant = !!parents?.estEnfant;
  const repart = useMemo(() => calculerRepartition(parJour, bornes.debut, bornes.fin), [parJour, bornes]);
  const parMois = useMemo(() => repartitionParMois(parJour, bornes.debut, bornes.fin), [parJour, bornes]);
  const maxMois = Math.max(1, ...parMois.map(m => m.total));
  const moisCourts = t('commun.moisCourts') || [];

  const nomP1 = parents?.parent1?.nom || t('stats.parent1');
  const nomP2 = parents?.parent2?.nom || t('stats.parent2');

  const chargement = donnees === null;

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
      <View style={s.page}>
        <PageHeader titre={t('stats.titre')} iconeTitre="📊" onRetour={onFermer} />

        {!chargement && !estEnfant ? (
          <View style={s.vide}>
            <Text style={{ fontSize: 40 }}>📊</Text>
            <Text style={s.videTxt}>{t('stats.aucunCalendrierEnfant')}{'\n'}{t('stats.statsEnfantSeulement')}</Text>
          </View>
        ) : (
          <ScrollView contentContainerStyle={s.corps}>
            <View style={s.periodeRow}>
              {PERIODES_STATS.map(p => (
                <TouchableOpacity key={p.id} style={[s.perChip, periode === p.id && s.perChipOn]} activeOpacity={0.7}
                  onPress={() => setPeriode(p.id)}>
                  <Text style={[s.perTxt, periode === p.id && s.perTxtOn]}>{t('stats.per_' + p.id)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {!!erreur && (
              <View style={s.bandeau}>
                <Text style={s.bandeauTxt}>⚠️ {t('stats.erreurLecture')}</Text>
                <Text style={s.bandeauDet}>{erreur}</Text>
              </View>
            )}

            {chargement ? (
              <ActivityIndicator color={UI.accent} style={{ marginTop: 30 }} />
            ) : erreur ? null : repart.total === 0 ? (
              <EmptyState icon="📊" texte={t('stats.aucunJourPeriode')} />
            ) : (
              <>
                <View style={s.card}>
                  <Text style={s.cardTitre}>{t('stats.repartitionTemps')}</Text>
                  <View style={s.barGlobale}>
                    <View style={[s.barPart, { flex: Math.max(repart.pourcentP1, 1), backgroundColor: COUL_P1 }]}>
                      {repart.pourcentP1 >= 12 && <Text style={s.barPct}>{repart.pourcentP1}%</Text>}
                    </View>
                    <View style={[s.barPart, { flex: Math.max(repart.pourcentP2, 1), backgroundColor: COUL_P2 }]}>
                      {repart.pourcentP2 >= 12 && <Text style={s.barPct}>{repart.pourcentP2}%</Text>}
                    </View>
                  </View>
                  <View style={{ gap: 6 }}>
                    <View style={s.legende}>
                      <View style={[s.dot, { backgroundColor: COUL_P1 }]} />
                      <Text style={s.legendeTxt}>{t('stats.legende', { nom: nomP1, jours: repart.p1, pct: repart.pourcentP1 })}</Text>
                    </View>
                    <View style={s.legende}>
                      <View style={[s.dot, { backgroundColor: COUL_P2 }]} />
                      <Text style={s.legendeTxt}>{t('stats.legende', { nom: nomP2, jours: repart.p2, pct: repart.pourcentP2 })}</Text>
                    </View>
                  </View>
                  <Text style={s.totalTxt}>
                    {repart.total > 1 ? t('stats.joursRenseignes', { n: repart.total }) : t('stats.jourRenseigne', { n: repart.total })}
                    {repart.nonRenseignes > 0 ? (repart.nonRenseignes > 1 ? t('stats.nonRenseignes', { n: repart.nonRenseignes }) : t('stats.nonRenseigne', { n: repart.nonRenseignes })) : ''}
                  </Text>
                </View>

                {parMois.length > 0 && (
                  <View style={s.card}>
                    <Text style={s.cardTitre}>{t('stats.parMois')}</Text>
                    {parMois.map(m => (
                      <View key={m.cle} style={s.moisRow}>
                        <Text style={s.moisLabel}>{moisCourts[m.mois] || ''}</Text>
                        <View style={s.moisBar}>
                          <View style={[s.moisSeg, { flex: m.p1, backgroundColor: COUL_P1 }]} />
                          <View style={[s.moisSeg, { flex: m.p2, backgroundColor: COUL_P2 }]} />
                          <View style={{ flex: Math.max(maxMois - m.total, 0) }} />
                        </View>
                        <Text style={s.moisVal}>{m.p1}/{m.p2}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </>
            )}
          </ScrollView>
        )}
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page: { flex: 1, backgroundColor: UI.bg },
  vide: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingHorizontal: 30 },
  videTxt: { fontSize: 14, color: UI.faint, textAlign: 'center', lineHeight: 20 },
  corps: { padding: 14, paddingBottom: 30 },
  periodeRow: { flexDirection: 'row', gap: 8, marginBottom: 14 },
  perChip: { flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 10, borderWidth: 1, borderColor: UI.accent },
  perChipOn: { backgroundColor: UI.accent },
  perTxt: { fontSize: 13, fontWeight: '700', color: UI.accent },
  perTxtOn: { color: '#fff' },
  card: { backgroundColor: UI.card, borderRadius: 14, padding: 16, marginBottom: 14, borderWidth: 0.5, borderColor: UI.border },
  cardTitre: { fontSize: 14, fontWeight: '700', color: UI.ink, marginBottom: 12 },
  barGlobale: { flexDirection: 'row', height: 34, borderRadius: 10, overflow: 'hidden', marginBottom: 12 },
  barPart: { alignItems: 'center', justifyContent: 'center' },
  barPct: { color: '#fff', fontSize: 13, fontWeight: '800' },
  legende: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 12, height: 12, borderRadius: 6 },
  legendeTxt: { fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  totalTxt: { fontSize: 12, color: UI.faint, marginTop: 10 },
  moisRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 9 },
  moisLabel: { width: 40, fontSize: 12, color: UI.muted, fontWeight: '600' },
  moisBar: { flex: 1, flexDirection: 'row', height: 14, borderRadius: 7, overflow: 'hidden', backgroundColor: UI.border },
  moisSeg: {},
  moisVal: { width: 44, fontSize: 12, color: UI.muted, textAlign: 'right' },
  bandeau: { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
